import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common/Common";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { GitBranch, Plus, MoreHorizontal, Pencil, Trash2, Lock } from "lucide-react";

export default function RolesPage() {
  const { t, can } = useApp();
  const [roles, setRoles] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions").catch(() => ({ data: [] }))]);
      setRoles(r.data); setCatalog(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", permissions: [] }); setDialog(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description, permissions: [...r.permissions] }); setDialog(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/roles/${editing.id}`, form); toast.success("Role updated"); }
      else { await api.post("/roles", form); toast.success("Role created"); }
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doDelete = async (r) => {
    try { await api.delete(`/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setConfirm(null);
  };

  const togglePerm = (key) => setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key] }));
  const toggleModule = (mod, checked) => {
    const keys = mod.permissions.map((p) => p.key);
    setForm((f) => ({ ...f, permissions: checked ? [...new Set([...f.permissions, ...keys])] : f.permissions.filter((p) => !keys.includes(p)) }));
  };
  const isSuper = editing?.name === "SUPER_ADMINISTRATOR";

  return (
    <div>
      <PageHeader title={t("roleManagement")} subtitle={t("roles")}>
        {can("roles.create") && <Button size="sm" className="gap-2" onClick={openCreate} data-testid="create-role-btn"><Plus className="h-4 w-4" />{t("createRole")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        {roles === null ? <TableSkeleton /> : roles.length === 0 ? <EmptyState icon={GitBranch} title="No roles found." /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("roleName")}</TableHead><TableHead>{t("description")}</TableHead>
                <TableHead>{t("permissions")}</TableHead><TableHead>{t("users")}</TableHead>
                <TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {roles.map((r) => (
                  <TableRow key={r.id} data-testid={`role-row-${r.name}`}>
                    <TableCell className="font-medium flex items-center gap-2">{r.name}{r.name === "SUPER_ADMINISTRATOR" && <Lock className="h-3 w-3 text-muted-foreground" />}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-xs truncate">{r.description}</TableCell>
                    <TableCell><Badge variant="secondary">{r.permissions.length}</Badge></TableCell>
                    <TableCell>{r.user_count}</TableCell>
                    <TableCell><Badge variant={r.is_system_role ? "outline" : "default"} className="text-[10px]">{r.is_system_role ? t("systemRole") : t("customRole")}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`role-actions-${r.name}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can("roles.edit") && r.name !== "SUPER_ADMINISTRATOR" && <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`role-edit-${r.name}`}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>}
                          {can("roles.view") && r.name === "SUPER_ADMINISTRATOR" && <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`role-view-${r.name}`}><Pencil className="h-4 w-4 mr-2" />{t("view")}</DropdownMenuItem>}
                          {can("roles.delete") && !r.is_system_role && <DropdownMenuItem className="text-destructive" onClick={() => setConfirm(r)} data-testid={`role-delete-${r.name}`}><Trash2 className="h-4 w-4 mr-2" />{t("delete")}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? (isSuper ? t("view") : t("editRole")) : t("createRole")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div><Label>{t("roleName")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={isSuper} className="mt-1.5" data-testid="role-name-input" /></div>
            <div><Label>{t("description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={isSuper} className="mt-1.5" data-testid="role-desc-input" /></div>
          </div>
          <div className="space-y-4 mt-2">
            <Label>{t("permissions")}</Label>
            {catalog.map((mod) => {
              const keys = mod.permissions.map((p) => p.key);
              const allChecked = keys.every((k) => form.permissions.includes(k));
              return (
                <div key={mod.module} className="border border-border rounded-md p-3">
                  <label className="flex items-center gap-2 font-medium text-sm mb-2 cursor-pointer">
                    <Checkbox checked={allChecked} onCheckedChange={(c) => toggleModule(mod, c)} disabled={isSuper} data-testid={`perm-module-${mod.module}`} />
                    {mod.label}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-6">
                    {mod.permissions.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} disabled={isSuper} data-testid={`perm-${p.key}`} />
                        {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("close")}</Button>
            {!isSuper && <Button onClick={save} disabled={saving || !form.name} data-testid="role-save-btn">{t("save")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} destructive
        title="Delete role?" description={`Delete role "${confirm?.name}"?`} confirmLabel={t("delete")} onConfirm={() => doDelete(confirm)} />
    </div>
  );
}
